import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { errorToast, successToast } from "../../../helpers";
import { usePostChangePasswordMutation } from "../../../services/auth";
import Loader from "../../../helpers/constants/Loader";
import { generateEncryptedKeyBody } from "../../../utils/crypto";
import { CommonBody } from "../../../types/General";
import useTranslation from "../../../hooks/Translation";

const ChangePassword = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [changePassword, { isLoading }] = usePostChangePasswordMutation();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [old_password, setold_password] = useState<boolean>(false);
  const [cnfmPassword, setCnfmPassword] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      old_password: "",
      new_password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      old_password: Yup.string()
        .required(t.error.currentPassRequired)
        .min(8, t.error.min8), // Update to match expected criteria
      new_password: Yup.string()
        .required(t.error.newPassReq)
        .notOneOf([Yup.ref("old_password")], t.error.newCannotBeOld)
        .matches(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          t.error.passValid
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("new_password")], t.error.mustMatch)
        .required(t.error.confirmPassReq),
    }),

    onSubmit: async (values) => {
      formik.setSubmitting(true);
      let body = {
        old_password: values.old_password,
        new_password: values.new_password,
      };

      try {
        let encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await changePassword(encryptedBody).unwrap();
        if (response?.status === 200) {
          successToast(t.main.passChanged);
          navigate("/dashboard");
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
      }
      formik.setSubmitting(false);
    },
  });

  return (
    <Box
      component="main"
      className="auth_module"
      sx={{
        alignItems: "center",
        display: "flex",
        flexGrow: 1,
      }}
    >
      <Loader isLoad={isLoading} />
      <Container className="auth_cntnr" maxWidth="xs">
        <Box
          m={0}
          className="auth_logo"
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <figure className="logo_cntnr">
            <img src="/static/images/logo.png" alt="" />
          </figure>
        </Box>
        <div className="auth_box">
          <Box sx={{ mb: 3, textAlign: "center", position: "relative" }}>
            <IconButton
              onClick={() => navigate("/dashboard")}
              sx={{ p: 0, position: "absolute", left: 0, color: "#1d1d1d" }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ color: "#1d1d1d" }}>
              {t.main.changePassword}
            </Typography>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <FormControl sx={{ mb: 2, width: "100%" }}>
              <Typography className="custom_label">
                {t.main.currentPass} *
              </Typography>
              <TextField
                className="text_field"
                hiddenLabel
                placeholder={t.main.currentPass}
                fullWidth
                id="old_password"
                name="old_password"
                type={old_password ? "text" : "password"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.old_password}
                helperText={
                  formik.touched.old_password && formik.errors.old_password
                }
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setold_password(!old_password)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {old_password ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2, width: "100%" }}>
              <Typography className="custom_label">
                {t.main.newPassword} *
              </Typography>
              <TextField
                className="text_field"
                placeholder={t.main.newPassword}
                fullWidth
                hiddenLabel
                id="new_password"
                name="new_password"
                type={cnfmPassword ? "text" : "password"}
                value={formik.values.new_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.new_password && formik.errors.new_password
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setCnfmPassword(!cnfmPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {cnfmPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2, width: "100%" }}>
              <Typography className="custom_label">
                {t.main.confirmPass} *
              </Typography>
              <TextField
                className="text_field"
                placeholder={t.main.confirmPass}
                fullWidth
                hiddenLabel
                id="passwordConfirmation"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <Box sx={{ pt: 3 }}>
              <Button
                className="btn btn_primary"
                color="primary"
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                {t.main.save}
              </Button>
            </Box>
          </form>
        </div>
      </Container>
    </Box>
  );
};

export default ChangePassword;
